import React from 'react';
import { Box, Stack, alpha, type Theme } from '@mui/material';
import { GridToolbarContainer, GridToolbarQuickFilter, GridToolbarExport, GridToolbarColumnsButton } from '@mui/x-data-grid';

/**
 * Shared chrome for every DataGrid on the Contact Optimisation pages
 * (Pulse's opportunity table, Model's calibration/history tables, Queue's
 * ranked list), so a style change can't drift between tabs and every real
 * table looks and behaves the same -- same header/row styling, same
 * sort/filter/quick-search/CSV-export toolbar, attached to the table
 * itself rather than as a separate button elsewhere on the page. Modeled
 * on the Dialing Opportunity page's own table kit
 * (`pages/dialing-opportunity/components/dialOpTableKit.tsx`).
 */
export function buildContactOptGridSx(theme: Theme, opts: { dimmedRowClass?: string } = {}) {
  return {
    border: 0,
    borderRadius: 0,
    fontSize: '0.8125rem',
    '& .MuiDataGrid-columnHeaders': {
      bgcolor: theme.tokens?.surface?.sunken ?? 'action.hover',
      borderBottom: `1px solid ${theme.palette.divider}`,
      minHeight: '36px !important',
      maxHeight: '36px !important',
    },
    '& .MuiDataGrid-columnHeader': {
      '&:focus, &:focus-within': { outline: 'none' },
    },
    '& .MuiDataGrid-columnHeaderTitle': {
      fontWeight: 600,
      fontSize: '0.7rem',
      letterSpacing: '0.04em',
      textTransform: 'uppercase',
      color: 'text.secondary',
    },
    '& .MuiDataGrid-cell': {
      borderColor: theme.palette.divider,
      '&:focus, &:focus-within': { outline: 'none' },
    },
    ...(opts.dimmedRowClass ? { [`& .MuiDataGrid-row.${opts.dimmedRowClass}`]: { opacity: 0.6 } } : {}),
    '& .MuiDataGrid-row:hover': { bgcolor: alpha(theme.palette.primary.main, 0.04) },
    '& .MuiDataGrid-footerContainer': {
      borderTop: `1px solid ${theme.palette.divider}`,
      minHeight: 52,
    },
    '& .MuiTablePagination-root': { fontSize: '0.75rem' },
  } as const;
}

export const CONTACT_OPT_PAGE_SIZE_OPTIONS = [25, 50, 100, 250];

/**
 * Quick search + column visibility + (optionally) CSV export, attached
 * directly above the table it belongs to. Export respects whatever the
 * quick filter/sort/pagination currently has applied, since it's MUI's
 * own `GridToolbarExport`, not a hand-rolled CSV builder. Column
 * visibility is MUI's own `GridToolbarColumnsButton` too -- every column
 * stays available, a table just showing too many of them is a "hide the
 * ones you don't need" problem, not a "we chose wrong, delete it"
 * problem.
 *
 * Pass `showExport: false` for tables that instead surface their own
 * download icon button in the page header row right above the table
 * (Queue does this, matching Timing/Plan's pattern) -- keeps a single,
 * consistently-placed download affordance instead of two competing ones.
 */
export function makeContactOptGridToolbar(fileName: string, opts: { showExport?: boolean } = {}) {
  const showExport = opts.showExport ?? true;
  return function ContactOptGridToolbar() {
    return (
      <GridToolbarContainer sx={{ px: 2, py: 1, gap: 1.5, borderBottom: 1, borderColor: 'divider', justifyContent: 'space-between' }}>
        <Stack direction="row" spacing={0.5}>
          <GridToolbarColumnsButton slotProps={{ button: { size: 'small' } }} />
          {showExport && (
            <GridToolbarExport slotProps={{ button: { size: 'small' } }} csvOptions={{ fileName, allColumns: true }} />
          )}
        </Stack>
        <Box sx={{ width: { xs: '100%', sm: 280 } }}>
          <GridToolbarQuickFilter debounceMs={250} />
        </Box>
      </GridToolbarContainer>
    );
  };
}
